import React, { useState } from "react";
import moment from "moment";
import { PurchaseOrderButton, PurchaseOrderFooter, PurchaseOrderHeader, PurchaseOrderProductIdentity, PurchaseOrderSuccessModal } from "./PurchaseOrderDetailsElements";
import styles from "./PurchaseOrderDetails.module.scss";

const PurchaseOrderDisputeForm = ({
  purchaseOrder,
  totalWeight,
  submitting,
  failed,
  submitted,
  onBack,
  onSubmit,
  onSubmittedClose,
}) => {
  const items = purchaseOrder.purchase_order_items || [];
  const [proposedDeliveryDate, setProposedDeliveryDate] = useState(
    purchaseOrder.expected_delivery_date
      ? moment(purchaseOrder.expected_delivery_date).format("YYYY-MM-DD")
      : ""
  );
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");
  const [proposedPrices, setProposedPrices] = useState(() =>
    Object.fromEntries(
      items.map((item) => [
        item.id,
        String(Number(item.price_per_unit || 0)),
      ])
    )
  );

  const submitDispute = () => {
    const isValid =
      Boolean(proposedDeliveryDate) &&
      items.length > 0 &&
      items.every((item) => Number(proposedPrices[item.id]) > 0);
    if (!isValid) {
      setError("Add a delivery date and a valid proposed price for every product.");
      return;
    }

    setError("");
    onSubmit({
      purchase_order_id: purchaseOrder.id,
      proposed_delivery_date: proposedDeliveryDate,
      remarks: remarks.trim(),
      items: items.map((item) => ({
        purchase_order_item_id: item.id,
        product_number: item.product_number,
        proposed_price: Number(proposedPrices[item.id]),
      })),
    });
  };

  return (
    <main className={styles.page}>
      <PurchaseOrderHeader
        number={purchaseOrder.purchase_order_number}
        summary={`${items.length} SKUs • ${totalWeight.toLocaleString("en-IN", { maximumFractionDigits: 2 })} kg`}
        onBack={onBack}
      />

      <div className={styles.content}>
        <h2 className="fs-18 fw-700" style={{ margin: "0 0 14px" }}>Raise dispute</h2>

        <section className={styles.formSection}>
          <label className={styles.fieldLabel} htmlFor="proposed-delivery-date">Suggest delivery date</label>
          <input
            id="proposed-delivery-date"
            className={styles.dateInput}
            type="date"
            min={moment().format("YYYY-MM-DD")}
            value={proposedDeliveryDate}
            onChange={(event) => setProposedDeliveryDate(event.target.value)}
          />
        </section>

        <section className={styles.formSection}>
          <span className={styles.fieldLabel}>Product details</span>
          <div className={styles.disputeProductList}>
            {items.map((item, index) => (
              <article className={styles.disputeProduct} key={item.id || item.product_number || index}>
                <PurchaseOrderProductIdentity item={item} />
                <div className={styles.disputeProductFields}>
                  <label className={`d-flex flex-column ${styles.disputeField}`}>
                    Order quantity
                    <input
                      className={styles.disputeInput}
                      disabled
                      value={`${Number(item.quantity_ordered || 0)} ${item.buy_measurement_unit || ""}`}
                    />
                  </label>
                  <label className={`d-flex flex-column ${styles.disputeField}`}>
                    Proposed price
                    <input
                      className={styles.disputeInput}
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={proposedPrices[item.id] || ""}
                      onChange={(event) =>
                        setProposedPrices((prices) => ({
                          ...prices,
                          [item.id]: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.formSection}>
          <label className={styles.fieldLabel} htmlFor="dispute-remarks">Share details about the dispute (optional)</label>
          <textarea
            id="dispute-remarks"
            className={styles.remarksInput}
            value={remarks}
            placeholder="Type dispute details..."
            onChange={(event) => setRemarks(event.target.value)}
          />
        </section>

        {(error || failed) && (
          <p className="fs-12 text-red-500" style={{ margin: "0 0 12px" }}>
            {error || "Could not submit the dispute. Please try again."}
          </p>
        )}
      </div>

      <PurchaseOrderFooter single>
        <PurchaseOrderButton type="button" disabled={submitting} onClick={submitDispute}>
          {submitting ? "Submitting..." : "Submit dispute"}
        </PurchaseOrderButton>
      </PurchaseOrderFooter>

      <PurchaseOrderSuccessModal visible={submitted} title="Dispute raised" onClose={onSubmittedClose} />
    </main>
  );
};

export default PurchaseOrderDisputeForm;
